import React from "react";
import { Music2, Users, Disc, CopyX, Tags, Database, Wand2, ListMusic, FolderCog, Share2, SearchCheck, UploadCloud } from "lucide-react";

const fmtBytes = (b) => {
  if (!b) return "0 MB";
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

const STATS = [
  ["total_tracks", "Total Tracks", Music2, "from-amber-500/20 to-orange-600/10 text-brand-amber"],
  ["artists", "Artists", Users, "from-sky-500/20 to-blue-600/10 text-sky-300"],
  ["albums", "Albums", Disc, "from-yellow-500/20 to-amber-600/10 text-yellow-300"],
  ["duplicate_candidates", "Duplicate Candidates", CopyX, "from-rose-500/20 to-pink-600/10 text-rose-300"],
  ["categories", "Genre Categories", Tags, "from-purple-500/20 to-fuchsia-600/10 text-purple-300"],
  ["storage_bytes", "Library Storage", Database, "from-cyan-500/20 to-teal-600/10 text-cyan-300"],
];

const ACTIONS = [
  ["Auto Tagging", "Identify & fix metadata", Wand2, "text-purple-300 border-purple-500/40", "autotag"],
  ["Find Duplicates", "Review identical files", SearchCheck, "text-emerald-300 border-emerald-500/40", "duplicates"],
  ["Create Playlists", "Build radio playlists", ListMusic, "text-sky-300 border-sky-500/40", "playlists"],
  ["Smart Organize", "Genre, decade, station", FolderCog, "text-brand-amber border-amber-500/40", "genres"],
  ["Export to Radio", "SAM, Myriad, RadioDJ+", Share2, "text-rose-300 border-rose-500/40", "export"],
];

export const OrganizerOverview = ({ overview, onImport, onNavigate, onOpenLibrary }) => {
  const o = overview || {};
  return (
    <div className="space-y-5" data-testid="ml-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {STATS.map(([key, label, Icon, cls]) => (
          <div key={key} className={`rounded-xl border border-line bg-gradient-to-br ${cls.split(" text-")[0]} bg-bg-surface p-4`}
            data-testid={`ml-stat-${key}`}>
            <div className={`w-9 h-9 rounded-lg border border-line flex items-center justify-center mb-3 ${"text-" + cls.split(" text-")[1]}`}>
              <Icon size={16} />
            </div>
            <div className="text-2xl font-bold text-ink-primary font-display">
              {key === "storage_bytes" ? fmtBytes(o[key]) : (o[key] ?? 0).toLocaleString()}
            </div>
            <div className="type-card-meta-label text-ink-muted mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {overview && o.total_tracks === 0 && (
        <div className="rounded-xl border border-dashed border-line p-8 text-center" data-testid="ml-empty-state">
          <Music2 size={28} className="mx-auto text-ink-muted mb-3" />
          <div className="type-section-heading text-ink-primary mb-1">Your music library is empty</div>
          <p className="type-body text-ink-muted mb-4">
            Import audio files or sync your Rip &amp; Tag Studio tracks — everything lands here, organized and radio-ready.
          </p>
          <button onClick={onImport} data-testid="ml-empty-import-btn"
            className="type-button-label inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-bg-base hover:opacity-90">
            <UploadCloud size={14} /> Import Music
          </button>
        </div>
      )}

      {overview && o.total_tracks > 0 && (
        <button onClick={() => onOpenLibrary({})} data-testid="ml-open-library-btn"
          className="type-button-label rounded-md border border-line px-3 py-2 text-ink-primary hover:bg-white/5">
          Open Music Library → {o.total_tracks.toLocaleString()} tracks
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {ACTIONS.map(([title, sub, Icon, cls, target]) => (
          <button key={title} onClick={() => onNavigate(target)} data-testid={`ml-action-${target}`}
            className={`rounded-xl border ${cls.split(" ")[1]} bg-bg-surface p-4 text-left hover:bg-white/5 transition`}>
            <Icon size={16} className={cls.split(" ")[0]} />
            <div className="type-list-title text-ink-primary mt-2">{title}</div>
            <div className="type-list-subtitle text-ink-muted">{sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
};
